import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Button, Card, PageHeader, Badge, EmptyState, Input, Select } from '../../components/ui/UI';
import { Users, RefreshCw, Trash2 } from 'lucide-react';
import './ObservationsPage.css';

const BUS = ['SNACKS', 'DAIRIES', 'SWAN'];
const DEPTS = ['Human Resources', 'Engineering', 'Finance', 'Marketing', 'Sales', 'Information Technology', 'Risk & Compliance', 'Supply Chain', 'Security', 'Corporate Affairs', 'HSE', 'Quality Assurance', 'Production', 'Legal', 'Management'];

export default function ObservationsPage() {
  const navigate = useNavigate();
  const [observers, setObservers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [searchName, setSearchName] = useState('');
  const [filterBusinessUnit, setFilterBusinessUnit] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Get all observations and group by observer
      const { data } = await api.get('/observations?limit=10000');
      
      if (data.observations && data.observations.length > 0) {
        // Group observations by observer
        const observerMap = {};
        data.observations.forEach(obs => {
          const key = obs.fullName; // Group by name
          if (!observerMap[key]) {
            observerMap[key] = {
              id: obs._id, // Use first observation ID - ideally would use observer ID from backend
              fullName: obs.fullName,
              email: obs.email,
              businessUnit: obs.businessUnit,
              department: obs.department,
              designation: obs.designation,
              submissionCount: 0,
              lastSubmission: null,
              recentBehaviorType: null,
            };
          }
          observerMap[key].submissionCount++;
          
          // Track most recent submission
          if (!observerMap[key].lastSubmission || new Date(obs.observationDate) > new Date(observerMap[key].lastSubmission)) {
            observerMap[key].lastSubmission = obs.observationDate;
            observerMap[key].recentBehaviorType = obs.behaviorType;
          }
        });
        
        // Convert to array and sort by most recent
        const observerList = Object.values(observerMap)
          .sort((a, b) => new Date(b.lastSubmission || 0) - new Date(a.lastSubmission || 0));
        
        setObservers(observerList);
      }
    } catch (err) {
      toast.error('Failed to load observers');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredObservers = observers.filter(obs => {
    const matchesName = obs.fullName.toLowerCase().includes(searchName.toLowerCase());
    const matchesBU = !filterBusinessUnit || obs.businessUnit === filterBusinessUnit;
    const matchesDept = !filterDepartment || obs.department === filterDepartment;
    return matchesName && matchesBU && matchesDept;
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [searchName, filterBusinessUnit, filterDepartment]);

  const totalPages = Math.max(1, Math.ceil(filteredObservers.length / rowsPerPage));
  const pagedObservers = filteredObservers.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  const handleObserverClick = (observer) => {
    // Navigate to observer room with the observer's fullName as identifier
    // Since backend doesn't return observer._id in observations, we'll use encoded fullName
    const observerId = encodeURIComponent(observer.fullName);
    navigate(`/admin/observations/observer/${observerId}`);
  };

  const handleDeleteObserver = async (observer) => {
    try {
      // Delete all observations for this observer
      const { data } = await api.get('/observations?limit=10000');
      const observerObservations = data.observations.filter(obs => obs.fullName === observer.fullName);
      
      // Delete each observation
      await Promise.all(
        observerObservations.map(obs => api.delete(`/observations/${obs._id}`))
      );
      
      // Remove observer from list
      setObservers(prev => prev.filter(o => o.fullName !== observer.fullName));
      setDeleteConfirm(null);
      toast.success(`Deleted ${observerObservations.length} observation${observerObservations.length !== 1 ? 's' : ''} from ${observer.fullName}`);
    } catch (err) {
      toast.error('Failed to delete observations');
      console.error(err);
    }
  };

  return (
    <div className="fade-in">
      <PageHeader
        title="Observations by Observer"
        subtitle={`${observers.length} observer${observers.length !== 1 ? 's' : ''}`}
        action={
          <Button variant="ghost" size="sm" icon={<RefreshCw size={13} />} onClick={fetchData}>
            Refresh
          </Button>
        }
      />

      {loading ? (
        <Card style={{ padding: 24 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[...Array(6)].map((_, i) => <div key={i} className="skeleton" style={{ height: 42 }} />)}
          </div>
        </Card>
      ) : observers.length === 0 ? (
        <Card>
          <EmptyState icon={<Users size={32} />} title="No observers yet" description="Observations will appear here as observers submit forms" />
        </Card>
      ) : (
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: 16, borderBottom: '1px solid var(--border)', backgroundColor: 'var(--bg-2)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
              <Input
                placeholder="Search by name..."
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                style={{ height: 36 }}
              />
              <Select value={filterBusinessUnit} onChange={(e) => setFilterBusinessUnit(e.target.value)} style={{ height: 36 }}>
                <option value="">All Business Units</option>
                {BUS.map(b => <option key={b} value={b}>{b}</option>)}
              </Select>
              <Select value={filterDepartment} onChange={(e) => setFilterDepartment(e.target.value)} style={{ height: 36 }}>
                <option value="">All Departments</option>
                {DEPTS.map(d => <option key={d} value={d}>{d}</option>)}
              </Select>
            </div>
          </div>
          {filteredObservers.length === 0 ? (
            <EmptyState icon={<Users size={32} />} title="No observers found" description="Try adjusting your search or filter criteria" />
          ) : (
          <>
          <div className="obs-table-wrap">
            <table className="observers-table">
              <thead>
                <tr>
                  <th>S/N</th>
                  <th>Observer</th>
                  <th>Business Unit</th>
                  <th>Department</th>
                  <th>Designation</th>
                  <th style={{ textAlign: 'center' }}>Submissions</th>
                  <th style={{ textAlign: 'center' }}>Recent Type</th>
                  <th style={{ textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagedObservers.map((observer, idx) => (
                  <tr 
                    key={observer.fullName}
                    onClick={() => handleObserverClick(observer)}
                    className="observer-row"
                  >
                    <td className="sn-cell">{idx + 1}</td>
                    <td className="observer-name-cell">{observer.fullName}</td>
                    <td>{observer.businessUnit}</td>
                    <td>{observer.department}</td>
                    <td>{observer.designation}</td>
                    <td style={{ textAlign: 'center' }}>
                      <Badge variant="accent">{observer.submissionCount}</Badge>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <Badge variant={observer.recentBehaviorType === 'Safe Behavior' ? 'safe' : 'risk'}>
                        {observer.recentBehaviorType === 'Safe Behavior' ? 'Safe' : 'At-Risk'}
                      </Badge>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <button 
                        className="delete-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteConfirm(observer);
                        }}
                        title="Delete all observations"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="pagination">
            <div>{`Showing ${filteredObservers.length === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1} - ${Math.min(currentPage * rowsPerPage, filteredObservers.length)} of ${filteredObservers.length}`}</div>
            <div className="pagination-controls">
              <button className="page-btn" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>First</button>
              <button className="page-btn" onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1}>Prev</button>
              <button className="page-btn page-btn--active" disabled>{currentPage}</button>
              <button className="page-btn" onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages}>Next</button>
              <button className="page-btn" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>Last</button>
            </div>
          </div>
          </>
          )}
        </Card>
      )}

      {/* Delete Confirm Modal */}
      {deleteConfirm && createPortal(
        <div className="modal-backdrop" onClick={() => setDeleteConfirm(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">Delete Observer</span>
              <button className="modal-close" onClick={() => setDeleteConfirm(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: '0.85rem', color: '#4b5563', marginBottom: 16 }}>
                Delete all observations from <strong>{deleteConfirm.fullName}</strong>? This action cannot be undone.
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <Button variant="secondary" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
                <Button variant="danger" onClick={() => handleDeleteObserver(deleteConfirm)}>Delete All</Button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
